import type { OnlineUserDto } from '../admin/dto';
import type { TaskDto } from '../tasks/dto';

// SYS-UC-13, ADR-012: сообщения соединения событий. Транспорт — WebSocket на
// ApiRoutes.system.realtime, тело — JSON. Новое событие — новый тип здесь и тема в хабе.

export enum RealtimeMessageType {
  /** Клиент → сервер: следить за блогом (null — ни за каким). */
  Subscribe = 'subscribe',
  /** Сервер → клиент: число публикаций блога, ожидающих выхода (PUB-UC-7). */
  DueCount = 'dueCount',
  /**
   * Сервер → клиент: проверка живости (SYS-UC-13.CC-2), клиент сразу отвечает Pong. Это сообщение
   * приложения, а не кадр ping протокола: IIS с ARR кадры управления не передаёт (ADR-012).
   */
  Ping = 'ping',
  /** Клиент → сервер: ответ на Ping. */
  Pong = 'pong',
  /** Сервер → клиент: изменилась публичная конфигурация — перечитать (CFG-UC-1). */
  ConfigChanged = 'configChanged',
  /** Клиент → сервер: администратор открыл (true) или закрыл (false) раздел «Онлайн» (ADM-UC-3). */
  WatchOnline = 'watchOnline',
  /**
   * Сервер → администратору, который следит за «Онлайн»: список сразу после подписки и после
   * каждого подключения, ухода или смены блога (ADM-UC-3, ADM-UC-3.CC-1).
   */
  OnlineUsers = 'onlineUsers',
  /**
   * Сервер → клиент сразу после подписки: фоновые задачи блога, незавершённые и недавно
   * завершённые, а администратору — ещё и задачи платформы (TASK-UC-2, ADR-019).
   */
  Tasks = 'tasks',
  /** Сервер → клиент: фоновая задача изменилась (TASK-UC-2). */
  TaskChanged = 'taskChanged',
}

export interface RealtimeSubscribe {
  type: RealtimeMessageType.Subscribe;
  blogId: string | null;
}

export interface RealtimeDueCount {
  type: RealtimeMessageType.DueCount;
  blogId: string;
  count: number;
}

export interface RealtimeConfigChanged {
  type: RealtimeMessageType.ConfigChanged;
}

export interface RealtimeWatchOnline {
  type: RealtimeMessageType.WatchOnline;
  watch: boolean;
}

export interface RealtimeOnlineUsers {
  type: RealtimeMessageType.OnlineUsers;
  generatedAt: string;
  users: OnlineUserDto[];
}

export interface RealtimeTasks {
  type: RealtimeMessageType.Tasks;
  /** Блог подписки; null — подписки на блог нет (у администратора — только задачи платформы). */
  blogId: string | null;
  tasks: TaskDto[];
}

export interface RealtimeTaskChanged {
  type: RealtimeMessageType.TaskChanged;
  task: TaskDto;
}

export interface RealtimePing {
  type: RealtimeMessageType.Ping;
}

export interface RealtimePong {
  type: RealtimeMessageType.Pong;
}

export type RealtimeClientMessage = RealtimeSubscribe | RealtimePong | RealtimeWatchOnline;
/** События для подписчиков в интерфейсе; служебный Ping клиент соединения обрабатывает сам. */
export type RealtimeEvent = RealtimeDueCount | RealtimeConfigChanged | RealtimeOnlineUsers | RealtimeTasks | RealtimeTaskChanged;
export type RealtimeServerMessage = RealtimeEvent | RealtimePing;

/** Коды закрытия соединения сервером (диапазон 4000–4999 — для приложений). */
export enum RealtimeCloseCode {
  /** Сессия истекла или отозвана — клиент выходит (SYS-UC-13.CC-2). */
  Unauthorized = 4401,
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Разбор сообщения клиента; всё, что не по контракту, — null. */
export function parseClientMessage(text: string): RealtimeClientMessage | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(value)) return null;
  if (value.type === RealtimeMessageType.Pong) return { type: RealtimeMessageType.Pong };
  if (value.type === RealtimeMessageType.WatchOnline) return typeof value.watch === 'boolean' ? { type: RealtimeMessageType.WatchOnline, watch: value.watch } : null;
  if (value.type !== RealtimeMessageType.Subscribe) return null;
  const { blogId } = value;
  if (blogId !== null && (typeof blogId !== 'string' || blogId.length === 0 || blogId.length > 64)) return null;
  return { type: RealtimeMessageType.Subscribe, blogId };
}

/** Разбор сообщения сервера; неизвестные типы (новее клиента) — null. */
export function parseServerMessage(text: string): RealtimeServerMessage | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(value)) return null;
  if (value.type === RealtimeMessageType.Ping) return { type: RealtimeMessageType.Ping };
  if (value.type === RealtimeMessageType.ConfigChanged) return { type: RealtimeMessageType.ConfigChanged };
  // Список собирает свой же сервер — проверяется только форма сообщения.
  if (value.type === RealtimeMessageType.OnlineUsers) {
    return typeof value.generatedAt === 'string' && Array.isArray(value.users)
      ? { type: RealtimeMessageType.OnlineUsers, generatedAt: value.generatedAt, users: value.users as OnlineUserDto[] }
      : null;
  }
  // Задачи собирает свой же сервер — проверяется только форма сообщения.
  if (value.type === RealtimeMessageType.Tasks) {
    return (value.blogId === null || typeof value.blogId === 'string') && Array.isArray(value.tasks)
      ? { type: RealtimeMessageType.Tasks, blogId: value.blogId, tasks: value.tasks as TaskDto[] }
      : null;
  }
  if (value.type === RealtimeMessageType.TaskChanged) {
    return isRecord(value.task) && typeof value.task.id === 'string' ? { type: RealtimeMessageType.TaskChanged, task: value.task as unknown as TaskDto } : null;
  }
  if (value.type !== RealtimeMessageType.DueCount) return null;
  if (typeof value.blogId !== 'string' || typeof value.count !== 'number') return null;
  return { type: RealtimeMessageType.DueCount, blogId: value.blogId, count: value.count };
}
